import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { Component, Input, OnInit } from '@angular/core';
import { Liability } from 'src/app/liabilities/models/liability.model';
import { LiabilitiesDelegateMode } from '../../models/liabilities-delegate-mode.enum';
import { LiabilitiesDelegateService } from '../../services/liabilities-delegate.service';
import { CustomerBiller } from '../../../billers/models/model';
import { SubAccount } from '../../../billers/models/sub-account.model';
import { LiabilitiesDelegateForm } from '../../forms/liabilities-delegate-form';
import { LiabilitiesDelegateSelectOptionType } from '../../models/liabilities-delegate-select-option-type.enum';
import { LiabilitiesDelegateSelectOption } from '../../models/liabilities-delegate-select-option.model';
import format from '../../utils/biller-subaccount-input-formatter';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
standalone: false,
  selector: 'app-liabilities-delegate',
  templateUrl: './liabilities-delegate.component.html',
  styleUrls: ['./liabilities-delegate.component.scss'],
})
export class LiabilitiesDelegateComponent implements OnInit {
  @Input() public liability: Liability;
  @Input() public customerBiller: CustomerBiller;
  @Input() public subAccount: SubAccount;

  public formName = 'delegateLiabilities';
  public liabilitiesDelegateMode = LiabilitiesDelegateMode;
  public errors = [];
  public errorMessages = {
    SAME_EMAIL_AS_CURRENT_USER: 'Nie można przekazać opłat na swój adres e-mail',
  };

  private SNACKBAR_DURATION = 5000;

  public form: LiabilitiesDelegateForm;
  public inputDisplayName: string;
  public cancelUrl: string;
  public selectOptions: LiabilitiesDelegateSelectOption[] = [];
  public mode: LiabilitiesDelegateMode;

  constructor(
    private liabilitiesDelegateService: LiabilitiesDelegateService,
    private router: Router,
    private snackbar: MatSnackBar,
  ) {}

  public ngOnInit(): void {
    if (this.liability && this.customerBiller) {
      this.fillSelectOptionsForLiability();
      this.mode = LiabilitiesDelegateMode.SINGLE;
      this.cancelUrl = '/liabilities';
    } else if (this.customerBiller && this.subAccount) {
      this.fillSelectOptionsForSubAccount();
      this.mode = LiabilitiesDelegateMode.SINGLE;
      this.cancelUrl = '/billers';
    } else {
      this.fillSelectOptionsForCustomerBiller();
      this.mode = LiabilitiesDelegateMode.MULTIPLE;
      this.cancelUrl = '/billers';
    }

    this.form.sources.valueChanges.subscribe((sources) => {
      if (this.mode === LiabilitiesDelegateMode.MULTIPLE) {
        this.inputDisplayName = this.getInputDisplayName(sources);
      }
    });
  }

  private fillSelectOptionsForLiability() {
    const liabilityOption = {
      id: this.liability.id,
      name: this.getLiabilitySingleDescription(),
      type: LiabilitiesDelegateSelectOptionType.LIABILITY,
    };

    const customerBillerOption = {
      id: this.customerBiller.id,
      name: this.getCustomerBillerSingleDescription(),
      type: LiabilitiesDelegateSelectOptionType.BILLER,
    };

    const subAccountOptions = [];
    if (this.customerBiller.subAccounts.length > 0) {
      subAccountOptions.push({
        id: this.liability.subAccount.id,
        name: this.getCustomerBillerSubAccountDescription(this.liability.subAccount),
        type: LiabilitiesDelegateSelectOptionType.SUBACCOUNT,
      });
    }

    this.selectOptions.push(liabilityOption, customerBillerOption, ...subAccountOptions);
    this.form = new LiabilitiesDelegateForm(this.customerBiller.id, [liabilityOption]);
  }

  private fillSelectOptionsForCustomerBiller() {
    if (this.customerBiller.subAccounts.length === 1) {
      this.fillBillerOptionWithOneSubaccount();
    } else if (this.customerBiller.subAccounts.length > 1) {
      this.fillBillerOptionWithMultipleSubaccounts();
    } else {
      this.fillBillerOptionWithoutSubaccount();
    }
  }

  private fillBillerOptionWithOneSubaccount() {
    const subAccount = this.customerBiller.subAccounts[0];
    const billerOption = {
      id: this.customerBiller.id,
      name: this.getSingleSubAccountDescription(subAccount),
      type: LiabilitiesDelegateSelectOptionType.BILLER,
    };

    this.selectOptions.push(billerOption);
    this.form = new LiabilitiesDelegateForm(this.customerBiller.id, [billerOption]);
    this.inputDisplayName = billerOption.name;
  }

  private fillBillerOptionWithMultipleSubaccounts() {
    const subAccountsOptions = [];
    this.customerBiller.subAccounts.forEach((subAccount) =>
      subAccountsOptions.push({
        id: subAccount.id,
        name: this.getSingleSubAccountDescription(subAccount),
        type: LiabilitiesDelegateSelectOptionType.SUBACCOUNT,
      }),
    );

    this.selectOptions.push(...subAccountsOptions);
    this.form = new LiabilitiesDelegateForm(this.customerBiller.id, subAccountsOptions);
    this.inputDisplayName = this.getInputDisplayName(subAccountsOptions);
  }

  private fillBillerOptionWithoutSubaccount() {
    const billerOption = {
      id: this.customerBiller.id,
      name: this.getCustomerBillerSingleDescription(),
      type: LiabilitiesDelegateSelectOptionType.BILLER,
    };

    this.selectOptions.push(billerOption);
    this.form = new LiabilitiesDelegateForm(this.customerBiller.id, [billerOption]);
    this.inputDisplayName = this.getInputDisplayName([billerOption]);
  }

  private fillSelectOptionsForSubAccount() {
    const subAccountOption = {
      id: this.subAccount.id,
      name: this.getSingleSubAccountDescription(this.subAccount),
      type: LiabilitiesDelegateSelectOptionType.SUBACCOUNT,
    };

    this.selectOptions.push(subAccountOption);
    this.form = new LiabilitiesDelegateForm(this.customerBiller.id, [subAccountOption]);
  }

  private getCustomerBillerSubAccountDescription(subAccount: SubAccount): string {
    return `Tylko opłaty od ${this.customerBiller.biller.name} (${subAccount.name})`;
  }

  private getCustomerBillerSingleDescription(): string {
    return `Wszystkie opłaty od ${this.customerBiller.biller.name}`;
  }

  private getSingleSubAccountDescription(subAccount: SubAccount): string {
    return `${this.customerBiller.biller.name} (${subAccount.name})`;
  }

  private getLiabilitySingleDescription(): string {
    return `Tylko wybrana opłata (${this.liability.title})`;
  }

  private getInputDisplayName(selectedOptions: LiabilitiesDelegateSelectOption[] = []): string {
    const selectedIds = selectedOptions
      .filter((o) => o.type === LiabilitiesDelegateSelectOptionType.SUBACCOUNT)
      .map((o) => o.id);

    const subAccountsNames = this.customerBiller.subAccounts
      .filter((sa) => selectedIds.includes(sa.id))
      .map((sa) => sa.name);

    const billerName = this.customerBiller.biller.name;

    return format(subAccountsNames, billerName);
  }

  public save() {
    const { type } = this.form.sources.value[0];

    switch (type) {
      case LiabilitiesDelegateSelectOptionType.LIABILITY:
        this.liabilitiesDelegateService
          .showDialogForLiabilityType(this.form, `${this.customerBiller.biller.name} (${this.liability.title})`)
          .pipe(catchError((error) => this.handleErrors(error)))
          .subscribe(() => {
            this.router.navigate(['/liabilities']);
            // this.snackbar.open(`Opłata została przekazana do ${this.form.emailAddress.value}`, null, {
            //   duration: this.SNACKBAR_DURATION,
            // });
          });
        break;
      case LiabilitiesDelegateSelectOptionType.BILLER:
        this.liabilitiesDelegateService
          .showDialogForBillerType(this.form, this.getInputDisplayName(this.form.sources.value))
          .pipe(catchError((error) => this.handleErrors(error)))
          .subscribe(() => {
            this.redirectToBillers();
            // this.snackbar.open(`Opłaty zostały przekazane do ${this.form.emailAddress.value}`, null, {
            //   duration: this.SNACKBAR_DURATION,
            // });
          });
        break;
      case LiabilitiesDelegateSelectOptionType.SUBACCOUNT:
        this.liabilitiesDelegateService
          .showDialogForSubAccountType(
            this.form,
            this.subAccount
              ? this.getSingleSubAccountDescription(this.subAccount)
              : this.getInputDisplayName(this.form.sources.value),
          )
          .pipe(catchError((error) => this.handleErrors(error)))
          .subscribe(() => {
            this.redirectToBillers();
            // this.snackbar.open(`Opłaty zostały przekazane do ${this.form.emailAddress.value}`, null, {
            //   duration: this.SNACKBAR_DURATION,
            // });
          });
        break;
      default:
        throwError('Unknow LiabilitiesDelegateSelectOptionType');
    }
  }

  private handleErrors(error: any): any {
    const { errorCodes } = error?.error;
    if (errorCodes) {
      this.errors = errorCodes;
      this.form.emailAddress.setErrors({});
    }
    throw error;
  }

  private redirectToBillers() {
    this.router.navigate(['/billers']);
  }

  public compareObjects(o1: any[], o2: any[]): boolean {
    return o1 && o2 && o1[0].id === o2[0].id && o1[0].displayName === o2[0].displayName;
  }
}
